import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ZeitschritteData } from '../../../../../types/common';
import { zeitschritteApi } from '@/api';
import { UseAllgemeineDatenReturn } from '../types';

/**
 * Custom hook for AllgemeineDaten state management, data fetching, and auto-save
 */
export const useAllgemeineDaten = (
  zeitschritte: ZeitschritteData,
  onChange: (data: ZeitschritteData) => void,
  sessionId?: string,
  onDirtyChange?: (isDirty: boolean) => void
): UseAllgemeineDatenReturn => {
  const { t } = useTranslation('training');

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isDirty, setIsDirty] = useState<boolean>(false);

  // Track previous isDirty to avoid unnecessary callbacks
  const prevIsDirtyRef = useRef<boolean>(false);

  // FIX-8 (API storm): AbortController + mounted guard for fetchZeitschritte
  // cleanup. Prevents fetch-after-unmount warnings and stale state writes when
  // sessionId changes rapidly.
  const fetchAbortRef = useRef<AbortController | null>(null);
  const isMountedRef = useRef<boolean>(true);

  // FIX-8 (API storm): Stable refs so the fetch effect doesn't re-fire when
  // i18n re-inits or parent re-renders cause onChange identity to change.
  const onChangeRef = useRef(onChange);
  const tRef = useRef(t);

  useEffect(() => {
    onChangeRef.current = onChange;
    tRef.current = t;
  }, [onChange, t]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (fetchAbortRef.current) {
        fetchAbortRef.current.abort();
      }
    };
  }, []);

  // Notify parent when isDirty changes
  useEffect(() => {
    if (prevIsDirtyRef.current !== isDirty && onDirtyChange) {
      onDirtyChange(isDirty);
    }
    prevIsDirtyRef.current = isDirty;
  }, [isDirty, onDirtyChange]);

  // Function to fetch data from backend API
  const fetchZeitschritte = useCallback(async (sid: string) => {
    // Abort any in-flight fetch before starting a new one
    if (fetchAbortRef.current) {
      fetchAbortRef.current.abort();
    }
    const controller = new AbortController();
    fetchAbortRef.current = controller;

    setLoading(true);
    setError(null);

    try {
      const data = await zeitschritteApi.getZeitschritte(sid);

      if (controller.signal.aborted || !isMountedRef.current) return;

      if (data) {
        const zeitschritteData = {
          eingabe: data.eingabe || '',
          ausgabe: data.ausgabe || '',
          zeitschrittweite: data.zeitschrittweite || '',
          offset: data.offset || ''
        };
        onChangeRef.current(zeitschritteData);
      } else {
        const emptyData = {
          eingabe: '',
          ausgabe: '',
          zeitschrittweite: '',
          offset: ''
        };
        onChangeRef.current(emptyData);
      }
    } catch (err: unknown) {
      // Swallow aborts (expected on unmount / sessionId change)
      if (controller.signal.aborted || (err instanceof Error && err.name === 'AbortError')) {
        return;
      }
      if (!isMountedRef.current) return;
      setError((err as Error)?.message || tRef.current('allgemeineDaten.errors.loading'));
    } finally {
      if (isMountedRef.current && !controller.signal.aborted) {
        setLoading(false);
        setIsDirty(false); // Data freshly loaded, not dirty
      }
    }
  }, []);

  // Fetch data from backend if sessionId is present
  useEffect(() => {
    if (sessionId) {
      fetchZeitschritte(sessionId);
    }
  }, [sessionId, fetchZeitschritte]);

  // Handler for value changes
  const handleChange = useCallback((type: keyof ZeitschritteData, value: string) => {
    const updatedData = {
      ...zeitschritte,
      [type]: value
    };
    onChange(updatedData);
    setIsDirty(true); // Mark as dirty when user changes data
  }, [zeitschritte, onChange]);

  // Clear error handler
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Save data to backend
  const saveData = useCallback(async (): Promise<boolean> => {
    if (!sessionId) return false;

    setIsSaving(true);
    setError(null);

    try {
      await zeitschritteApi.updateZeitschritte(sessionId, zeitschritte);
      setIsDirty(false); // Mark as clean after successful save
      return true;
    } catch (err: unknown) {
      setError((err as Error)?.message || t('allgemeineDaten.errors.saving'));
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [sessionId, zeitschritte, t]);

  return {
    loading,
    error,
    isSaving,
    isDirty,
    handleChange,
    clearError,
    saveData
  };
};
